import React from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Utensils, ShoppingCart, Clock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const mealPlanTypes = {
  'weight-loss': {
    title: 'Weight Loss Meal Plans',
    h1: 'AI-Powered Weight Loss Meal Plans',
    description: 'Personalized calorie-deficit meal plans for sustainable weight loss. Delicious recipes, macro-balanced nutrition, and shopping lists included.',
    keywords: 'weight loss meal plan, diet plan, calorie deficit meals, fat loss diet, healthy meal plan, weight loss recipes, diet food plan',
    benefits: ['Calorie-controlled portions', 'High protein for satiety', 'Balanced macros', 'Sustainable approach'],
  },
  'muscle-gain': {
    title: 'Muscle Gain Meal Plans',
    h1: 'High-Protein Meal Plans for Muscle Building',
    description: 'Bulking meal plans optimized for muscle growth. High protein recipes, caloric surplus guidance, and macro tracking for gains.',
    keywords: 'muscle building meal plan, bulking diet, high protein diet, bodybuilding meal plan, gain muscle diet, protein meal prep',
    benefits: ['Optimal protein timing', 'Caloric surplus guidance', 'Complex carbs for energy', 'Muscle recovery nutrition'],
  },
  'keto': {
    title: 'Keto Meal Plans',
    h1: 'AI-Generated Ketogenic Meal Plans',
    description: 'Low-carb, high-fat ketogenic meal plans. Stay in ketosis with delicious keto recipes and macro-tracked nutrition.',
    keywords: 'keto meal plan, ketogenic diet, low carb meal plan, keto recipes, keto diet plan, LCHF meal plan',
    benefits: ['Under 20g net carbs', 'High healthy fats', 'Moderate protein', 'Keto-friendly recipes'],
  },
  'vegan': {
    title: 'Vegan Meal Plans',
    h1: 'Plant-Based Vegan Meal Plans',
    description: '100% plant-based meal plans with complete nutrition. Protein-rich vegan recipes for athletes and fitness enthusiasts.',
    keywords: 'vegan meal plan, plant based diet, vegan fitness diet, vegan protein meals, vegan athlete diet, vegetarian meal plan',
    benefits: ['Complete plant proteins', 'B12 and iron sources', 'High fiber nutrition', 'Sustainable eating'],
  },
  'mediterranean': {
    title: 'Mediterranean Diet Meal Plans',
    h1: 'Mediterranean Diet Meal Plans',
    description: 'Heart-healthy Mediterranean meal plans. Olive oil, fish, vegetables, and whole grains for longevity and wellness.',
    keywords: 'mediterranean diet, mediterranean meal plan, heart healthy diet, olive oil diet, Greek diet, healthy eating plan',
    benefits: ['Heart-healthy fats', 'Anti-inflammatory foods', 'Whole food focus', 'Sustainable lifestyle'],
  },
  'high-protein': {
    title: 'High Protein Meal Plans',
    h1: 'High Protein Meal Plans for Athletes',
    description: 'Protein-optimized meal plans for athletes and active individuals. 1.6-2.2g protein per kg bodyweight recipes.',
    keywords: 'high protein meal plan, protein diet, athlete meal plan, sports nutrition, protein recipes, gym diet plan',
    benefits: ['1.6-2.2g/kg protein', 'Muscle recovery support', 'Athletic performance', 'Lean protein sources'],
  },
  'budget': {
    title: 'Budget-Friendly Meal Plans',
    h1: 'Healthy Eating on a Budget - Affordable Meal Plans',
    description: 'Nutritious meal plans that won\'t break the bank. Affordable ingredients, batch cooking, and smart shopping strategies.',
    keywords: 'cheap meal plan, budget meal prep, affordable healthy eating, cheap healthy meals, budget diet plan, meal plan on a budget',
    benefits: ['Under $50/week groceries', 'Batch cooking recipes', 'Pantry staples focus', 'Minimal food waste'],
  },
  'family': {
    title: 'Family Meal Plans',
    h1: 'Healthy Family Meal Plans - Kid-Friendly Nutrition',
    description: 'Nutritious meals the whole family will love. Kid-approved recipes, balanced nutrition, and efficient meal prep.',
    keywords: 'family meal plan, kid friendly meals, healthy family dinners, family meal prep, children nutrition, family diet plan',
    benefits: ['Kid-approved recipes', 'Balanced nutrition for all ages', 'One meal for everyone', 'Efficient cooking'],
  },
  'meal-prep': {
    title: 'Meal Prep Plans',
    h1: 'Weekly Meal Prep Plans - Batch Cooking Made Easy',
    description: 'Efficient meal prep plans for busy schedules. Cook once, eat healthy all week with organized batch cooking strategies.',
    keywords: 'meal prep plan, batch cooking, weekly meal prep, food prep, meal prep ideas, Sunday meal prep, meal prep for beginners',
    benefits: ['Cook once, eat all week', 'Time-saving strategies', 'Proper food storage', 'Reheating instructions'],
  },
  'diabetic': {
    title: 'Diabetic-Friendly Meal Plans',
    h1: 'Diabetic Meal Plans - Blood Sugar Balanced Nutrition',
    description: 'Blood sugar-friendly meal plans for diabetics and pre-diabetics. Low glycemic recipes with balanced macros.',
    keywords: 'diabetic meal plan, diabetes diet, low glycemic meal plan, blood sugar diet, diabetic recipes, diabetes nutrition',
    benefits: ['Low glycemic index foods', 'Balanced blood sugar', 'Fiber-rich meals', 'Portion guidance'],
  },
  'gluten-free': {
    title: 'Gluten-Free Meal Plans',
    h1: 'Gluten-Free Meal Plans - Celiac-Safe Nutrition',
    description: 'Delicious gluten-free meal plans for celiacs and gluten sensitivity. Naturally GF recipes and cross-contamination guidance.',
    keywords: 'gluten free meal plan, celiac diet, GF recipes, gluten free diet plan, wheat free meals, gluten intolerance diet',
    benefits: ['100% gluten-free recipes', 'Cross-contamination tips', 'Naturally GF ingredients', 'Celiac-safe preparation'],
  },
  'intermittent-fasting': {
    title: 'Intermittent Fasting Meal Plans',
    h1: 'Intermittent Fasting Meal Plans - IF Optimized Nutrition',
    description: 'Meal plans designed for intermittent fasting. 16:8, 18:6, and OMAD-friendly recipes with optimal eating windows.',
    keywords: 'intermittent fasting meal plan, IF diet, 16:8 diet, fasting meal plan, OMAD meals, time restricted eating',
    benefits: ['Eating window optimized', 'Nutrient-dense meals', 'Fasting support', 'Metabolic health focus'],
  },
};

export default function MealPlans() {
  const navigate = useNavigate();
  const { type } = useParams();
  
  const mealPlan = type ? mealPlanTypes[type] : null;

  if (mealPlan) {
    return (
      <>
        <SEO 
          title={mealPlan.title}
          description={mealPlan.description}
          keywords={mealPlan.keywords}
          url={`https://vyta.com/meal-plans/${type}`}
        />
        <div className="min-h-screen bg-black text-white">
          <header className="border-b border-zinc-800">
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
                  <span className="text-black font-black text-xl">V</span>
                </div>
                <span className="font-bold text-xl">VYTA</span>
              </Link>
              <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full">
                Start Free Trial
              </Button>
            </div>
          </header>

          <main className="max-w-4xl mx-auto px-6 py-16">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl sm:text-5xl font-black mb-6">{mealPlan.h1}</h1>
              <p className="text-xl text-zinc-400 mb-12">{mealPlan.description}</p>

              <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 mb-8">
                <h2 className="text-xl font-bold mb-4 text-[#14B8A6]">What You Get</h2>
                <ul className="space-y-3">
                  {mealPlan.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-12">
                <div className="p-4 rounded-xl bg-zinc-900 text-center">
                  <Utensils className="w-8 h-8 text-[#CCFF00] mx-auto mb-2" />
                  <p className="font-bold">Full Recipes</p>
                  <p className="text-sm text-zinc-400">With cooking instructions</p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900 text-center">
                  <ShoppingCart className="w-8 h-8 text-[#CCFF00] mx-auto mb-2" />
                  <p className="font-bold">Shopping Lists</p>
                  <p className="text-sm text-zinc-400">Quantities included</p>
                </div>
                <div className="p-4 rounded-xl bg-zinc-900 text-center">
                  <Heart className="w-8 h-8 text-[#CCFF00] mx-auto mb-2" />
                  <p className="font-bold">Your Preferences</p>
                  <p className="text-sm text-zinc-400">Foods you actually like</p>
                </div>
              </div>

              <div className="text-center p-8 rounded-2xl bg-gradient-to-r from-[#14B8A6]/10 to-[#CCFF00]/10 border border-[#14B8A6]/20">
                <h2 className="text-2xl font-bold mb-4">Get Your Personalized {mealPlan.title.replace(' Meal Plans', '')} Plan</h2>
                <p className="text-zinc-400 mb-6">AI creates meal plans based on YOUR tastes, allergies, and goals</p>
                <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 py-6 text-lg">
                  Start 7-Day Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </main>
        </div>
      </>
    );
  }

  // Index page
  return (
    <>
      <SEO 
        title="Meal Plans - All Diet Types"
        description="Browse all VYTA AI-powered meal plans: weight loss, muscle gain, keto, vegan, Mediterranean, and more. Personalized nutrition with recipes and shopping lists."
        keywords="meal plans, diet plans, nutrition plans, healthy eating, meal prep, AI meal planning, personalized diet"
        url="https://vyta.com/meal-plans"
      />
      <div className="min-h-screen bg-black text-white">
        <header className="border-b border-zinc-800">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
                <span className="text-black font-black text-xl">V</span>
              </div>
              <span className="font-bold text-xl">VYTA</span>
            </Link>
            <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full">
              Start Free Trial
            </Button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-black mb-4">AI-Powered <span className="text-[#14B8A6]">Meal Plans</span></h1>
            <p className="text-xl text-zinc-400">Find your perfect nutrition plan, personalized by AI</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(mealPlanTypes).map(([key, plan], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/meal-plans/${key}`}
                  className="block p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#14B8A6]/50 transition-colors"
                >
                  <h2 className="text-xl font-bold mb-2">{plan.title}</h2>
                  <p className="text-sm text-zinc-400 line-clamp-2">{plan.description}</p>
                  <span className="inline-flex items-center gap-1 mt-4 text-[#14B8A6] text-sm font-medium">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}
